/**
 * Pure helpers backing FormNumberInput's "draft string" editing model.
 *
 * Numeric fields cannot bind straight to a number: the moment a user clears the field the
 * parsed value is empty, and a parent that stores `0` for empty immediately paints `0` back
 * into the box (which is how "150" ends up typed as "0150"). These helpers keep the in-progress
 * text separate from the committed number so an empty field is allowed to stay empty.
 */

/** Renders a committed value as editable text. Empty/non-finite values render as an empty field. */
export const toNumericDraft = (value: number | null | undefined): string =>
    value === null || value === undefined || !Number.isFinite(value) ? '' : String(value);

/**
 * Parses an in-progress draft. Returns `null` for an empty field and for drafts that are not yet
 * a number (`'-'`, `'.'`), so the caller can distinguish "nothing entered" from a real value.
 */
export const parseNumericDraft = (draft: string): number | null => {
    if (draft.trim() === '') return null;

    const parsed = Number(draft);

    return Number.isFinite(parsed) ? parsed : null;
};

interface SanitizeNumericDraftOptions {
    integer: boolean;
    allowNegative: boolean;
}

/**
 * Coerces arbitrary typed or pasted text into a valid partial number, keeping the field usable
 * mid-edit (`''`, `'-'`, `'1.'` are all allowed). Strips grouping separators and currency symbols
 * so pasting "$1,200.50" works, and drops redundant leading zeros so appending to a `0` field
 * produces "150" rather than "0150".
 */
export const sanitizeNumericDraft = (raw: string, { integer, allowNegative }: SanitizeNumericDraftOptions): string => {
    const stripped = raw.replace(integer ? /[^\d-]/g : /[^\d.-]/g, '');
    const isNegative = allowNegative && stripped.startsWith('-');

    let digits = stripped.replace(/-/g, '');

    const firstDotIndex = digits.indexOf('.');

    if (firstDotIndex !== -1) {
        digits = `${digits.slice(0, firstDotIndex + 1)}${digits.slice(firstDotIndex + 1).replace(/\./g, '')}`;
    }

    // "0150" -> "150", "00" -> "0", while "0.5" keeps its leading zero.
    digits = digits.replace(/^0+(?=\d)/, '');

    return isNegative ? `-${digits}` : digits;
};

/** Restricts a value to the inclusive min/max window, ignoring bounds that were not supplied. */
export const clampNumber = (value: number, min: number | undefined, max: number | undefined): number => {
    let clamped = value;

    if (min !== undefined && clamped < min) clamped = min;
    if (max !== undefined && clamped > max) clamped = max;

    return clamped;
};

/**
 * Rounds a stepped value to the step's decimal precision so arrow-key stepping does not surface
 * binary floating point noise (0.1 + 0.2 -> 0.30000000000000004).
 */
export const roundToStepPrecision = (value: number, step: number): number => {
    const stepText = String(step);

    if (stepText.includes('e')) return value;

    const dotIndex = stepText.indexOf('.');

    if (dotIndex === -1) return value;

    return Number(value.toFixed(stepText.length - dotIndex - 1));
};
